import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { PhaseType } from '../phases/types';

export interface Shop {
  id: string;
  name: string;
  web: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  customerNo: string | null;
  discount: string | null;
  leadDays: number | null;
  minOrder: string | null;
  note: string | null;
  active: boolean;
  _count?: { sources: number; purchases: number };
}

export interface MaterialSource {
  id: string;
  materialId?: string;
  shopId: string;
  url: string | null;
  shopSku: string | null;
  packQty: string | null;
  packLabel: string | null;
  unitCost: string | null; // jen finance (jinak null)
  priceDate: string | null;
  currency: string;
  leadDays?: number | null;
  preferred: boolean;
  note?: string | null;
  shop: { id?: string; name: string; web?: string | null; leadDays?: number | null };
}

export type PurchaseStatus = 'open' | 'bought' | 'cancelled';

export interface PurchaseItem {
  id: string;
  materialId: string | null;
  name: string | null;
  qty: string;
  unit: string | null;
  shopId: string | null;
  phaseId: string | null;
  status: PurchaseStatus;
  note: string | null;
  receivedQty: string | null;
  boughtAt: string | null;
  createdAt: string;
  material: {
    id: string;
    name: string;
    sku: string;
    unit: string;
    stockQty: string;
    minQty: string | null;
    stockLocation: { code: string } | null;
    sources: MaterialSource[];
  } | null;
  shop: { id: string; name: string; web: string | null; customerNo: string | null } | null;
  phase: { id: string; type: PhaseType; propItem: { id: string; name: string; project: { code: string } } } | null;
  createdBy: { fullName: string } | null;
  boughtBy: { fullName: string } | null;
}

export const itemLabel = (p: PurchaseItem) => p.material?.name ?? p.name ?? '—';

// Zdroj pro položku: z obchodu položky, jinak preferovaný.
export const sourceFor = (p: PurchaseItem) =>
  p.material?.sources.find((s) => s.shopId === p.shopId) ?? p.material?.sources[0] ?? null;

const SHOPS = ['shops'] as const;
const PURCHASES = ['purchases'] as const;

export function useShops(includeInactive = false) {
  return useQuery({ queryKey: [...SHOPS, includeInactive], queryFn: () => api.get<Shop[]>(`/api/shops${includeInactive ? '?vse=1' : ''}`) });
}

// Kolik z koupeného ještě nedorazilo (volné položky se nesledují).
export const waitingQty = (p: PurchaseItem) =>
  p.status === 'bought' && p.material ? Math.max(0, Math.round((Number(p.qty) - Number(p.receivedQty ?? 0)) * 1000) / 1000) : 0;

export function usePurchases(stav: 'open' | 'bought' | 'waiting' | 'all' = 'open') {
  return useQuery({ queryKey: [...PURCHASES, stav], queryFn: () => api.get<PurchaseItem[]>(`/api/purchases?stav=${stav}`) });
}

export function useSources(materialId: string | null) {
  return useQuery({
    queryKey: ['material-sources', materialId],
    queryFn: () => api.get<MaterialSource[]>(`/api/materials/${materialId}/sources`),
    enabled: !!materialId,
  });
}

function useInvalidating<T, R>(fn: (v: T) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SHOPS });
      qc.invalidateQueries({ queryKey: PURCHASES });
      qc.invalidateQueries({ queryKey: ['material-sources'] });
      qc.invalidateQueries({ queryKey: ['materials'] }); // naskladnění z nákupu
    },
  });
}

export type ShopInput = Partial<Omit<Shop, 'id' | '_count'>> & { name: string };

export const useCreateShop = () => useInvalidating((v: ShopInput) => api.post<Shop>('/api/shops', v));
export const useUpdateShop = () => useInvalidating(({ id, ...v }: Partial<ShopInput> & { id: string }) => api.patch<Shop>(`/api/shops/${id}`, v));
export const useDeleteShop = () => useInvalidating((id: string) => api.del(`/api/shops/${id}`));

export interface SourceInput {
  shopId: string;
  url?: string;
  shopSku?: string;
  packQty?: number;
  packLabel?: string;
  unitCost?: number;
  leadDays?: number;
  preferred?: boolean;
  note?: string;
}
export const useUpsertSource = (materialId: string) =>
  useInvalidating((v: SourceInput) => api.post<MaterialSource>(`/api/materials/${materialId}/sources`, v));
export const useDeleteSource = () => useInvalidating((id: string) => api.del(`/api/material-sources/${id}`));

export interface PurchaseInput {
  materialId?: string;
  name?: string;
  qty: number;
  unit?: string;
  shopId?: string | null;
  phaseId?: string | null;
  note?: string;
}
export const useAddPurchase = () => useInvalidating((v: PurchaseInput) => api.post<PurchaseItem>('/api/purchases', v));
export const useUpdatePurchase = () =>
  useInvalidating(({ id, ...v }: { id: string; qty?: number; shopId?: string | null; status?: PurchaseStatus; note?: string }) =>
    api.patch<PurchaseItem>(`/api/purchases/${id}`, v));
export const useDeletePurchase = () => useInvalidating((id: string) => api.del(`/api/purchases/${id}`));
export const useMarkBought = () =>
  useInvalidating(({ id, ...v }: { id: string; receiveQty?: number; unitCost?: number }) => api.post<PurchaseItem>(`/api/purchases/${id}/bought`, v));
export const useReceivePurchase = () =>
  useInvalidating(({ id, ...v }: { id: string; qty: number; unitCost?: number }) => api.post<PurchaseItem>(`/api/purchases/${id}/receive`, v));
export const useCloseReceipt = () => useInvalidating((id: string) => api.post<PurchaseItem>(`/api/purchases/${id}/close-receipt`, {}));
export const useFillLowStock = () => useInvalidating(() => api.post<{ added: number }>('/api/purchases/fill-low-stock', {}));
